import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { API_ENDPOINT } from '../constants';

@customElement('add-balance-modal')
export class AddBalanceModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @state() private selectedAmount: number | null = null;
  @state() private loading = false;
  @state() private error = '';
  @state() private success = false;

  private amounts = [3, 5, 10, 15, 20];

  createRenderRoot() {
    return this;
  }

  render() {
    if (!this.open) return html``;

    return html`
      <div class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div class="bg-minecraft-netherite border-4 border-gray-600 shadow-minecraft-lg rounded-lg p-8 w-full max-w-md relative animate-slide-up">
          <!-- Close Button -->
          <button class="absolute top-4 right-4 w-10 h-10 rounded bg-minecraft-redstone border-2 border-minecraft-redstone/60 hover:bg-minecraft-redstone/80 text-white transition-all duration-300 transform hover:scale-110 flex items-center justify-center" @click=${this.close}>
            ✕
          </button>

          <!-- Header -->
          <div class="text-center mb-8">
            <div class="w-16 h-16 mx-auto mb-4 bg-minecraft-gold border-2 border-minecraft-gold/60 shadow-minecraft rounded flex items-center justify-center animate-bob">
              <span class="text-black text-2xl">💰</span>
            </div>
            <h2 class="text-3xl font-minecraft font-bold mb-2 text-minecraft-gold">
              ADD BALANCE
            </h2>
            <p class="text-gray-200">Choose an amount to add to your wallet</p>
          </div>

          ${this.success ? html`
            <div class="text-center">
              <div class="w-16 h-16 mx-auto mb-4 bg-minecraft-emerald border-2 border-minecraft-emerald/60 shadow-minecraft rounded flex items-center justify-center animate-bob">
                <span class="text-white text-2xl">✅</span>
              </div>
              <h3 class="text-xl font-minecraft font-bold mb-2 text-minecraft-emerald">SUCCESS!</h3>
              <p class="text-gray-200 mb-6">$${this.selectedAmount} has been added to your wallet</p>
              <button @click=${this.close} class="bg-minecraft-emerald hover:bg-minecraft-emerald/80 border-2 border-minecraft-emerald/60 shadow-minecraft px-6 py-3 rounded font-minecraft text-white transition-all duration-300 transform hover:scale-105">
                CONTINUE
              </button>
            </div>
          ` : html`
            <!-- Amount Selection -->
            <div class="grid grid-cols-2 gap-3 mb-6">
              ${this.amounts.map(amount => html`
                <button
                  @click=${() => this.selectAmount(amount)}
                  class="p-4 rounded border-2 transition-all duration-300 transform hover:scale-105 font-minecraft font-bold ${
                    this.selectedAmount === amount
                      ? 'bg-minecraft-emerald border-minecraft-emerald/60 text-white'
                      : 'bg-minecraft-stone border-gray-600 text-white hover:border-minecraft-emerald/30'
                  }"
                >
                  $${amount}
                </button>
              `)}
            </div>

            ${this.error ? html`
              <div class="bg-minecraft-redstone/20 border-2 border-minecraft-redstone/60 text-white text-sm p-3 rounded mb-4 flex items-center">
                <span class="mr-2">⚠️</span>
                ${this.error}
              </div>
            ` : ''}

            <!-- Action Buttons -->
            <div class="flex gap-3">
              <button
                @click=${this.close}
                class="flex-1 bg-minecraft-stone hover:bg-minecraft-stone/80 border-2 border-gray-600 shadow-minecraft px-4 py-3 rounded font-minecraft text-white transition-all duration-300 transform hover:scale-105"
              >
                CANCEL
              </button>
              <button
                @click=${this.addBalance}
                ?disabled=${!this.selectedAmount || this.loading}
                class="flex-1 bg-minecraft-gold hover:bg-minecraft-gold/80 border-2 border-minecraft-gold/60 shadow-minecraft px-4 py-3 rounded font-minecraft text-black transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ${this.loading ? 'ADDING...' : `ADD $${this.selectedAmount || 0}`}
              </button>
            </div>
          `}
        </div>
      </div>
    `;
  }

  private selectAmount(amount: number) {
    this.selectedAmount = amount;
    this.error = '';
  }

  private close() {
    this.open = false;
    this.selectedAmount = null;
    this.error = '';
    this.success = false;
    this.loading = false;
  }

  private async addBalance() {
    if (!this.selectedAmount) return;

    this.loading = true;
    this.error = '';

    try {
      const response = await fetch(API_ENDPOINT+'/api/wallet/add', {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          amount: this.selectedAmount
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add balance');
      }

      const data = await response.json();

      // Update wallet balance in the UI
      const walletBalanceElement = document.querySelector('#wallet-balance');
      if (walletBalanceElement) {
        walletBalanceElement.textContent = `$${data.balance.toFixed(2)}`;
      }

      this.success = true;

      // Dispatch event for other components to listen
      this.dispatchEvent(new CustomEvent('balance-updated', {
        detail: { balance: data.balance },
        bubbles: true
      }));

    } catch (error: any) {
      this.error = error.message || 'Failed to add balance';
    } finally {
      this.loading = false;
    }
  }

  show() {
    this.open = true;
  }
}
